import { useMemo, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface TagInputProps {
    value: string[];
    onChange: (next: string[]) => void;
    // Previously-used tags to offer as autocomplete suggestions.
    suggestions?: string[];
    placeholder?: string;
    disabled?: boolean;
    maxTags?: number;
}

// Normalize a tag for de-dupe/compare: trim + collapse whitespace + lowercase.
// Display keeps the user's original casing; matching is case-insensitive.
function normalize(tag: string): string {
    return tag.trim().replace(/\s+/g, " ").toLowerCase();
}

const DEFAULT_MAX_TAGS = 10;

export default function TagInput({
    value,
    onChange,
    suggestions = [],
    placeholder = "Add a tag and press Enter",
    disabled,
    maxTags = DEFAULT_MAX_TAGS,
}: TagInputProps) {
    const [draft, setDraft] = useState("");
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const atMax = value.length >= maxTags;

    const filteredSuggestions = useMemo(() => {
        const taken = new Set(value.map(normalize));
        const d = normalize(draft);
        return suggestions
            .filter((s) => !taken.has(normalize(s)))
            .filter((s) => (d ? normalize(s).includes(d) : true))
            .slice(0, 6);
    }, [suggestions, value, draft]);

    function addTag(raw: string) {
        const clean = raw.trim().replace(/\s+/g, " ");
        if (!clean || atMax) return;
        // Case-insensitive de-dupe.
        if (value.some((t) => normalize(t) === normalize(clean))) {
            setDraft("");
            return;
        }
        onChange([...value, clean]);
        setDraft("");
    }

    function removeTag(tag: string) {
        onChange(value.filter((t) => t !== tag));
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addTag(draft);
        } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
            // Backspace on empty input removes the last chip.
            removeTag(value[value.length - 1]);
        }
    }

    return (
        <div className="relative">
            <div
                className={cn(
                    "flex flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 transition-colors",
                    focused ? "border-blue-400 ring-1 ring-blue-100" : "border-gray-200",
                    disabled && "cursor-not-allowed opacity-60"
                )}
                onClick={() => inputRef.current?.focus()}
            >
                {value.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                    >
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(tag);
                                }}
                                className="text-blue-400 hover:text-blue-700"
                                aria-label={`Remove ${tag}`}
                            >
                                <XIcon className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={draft}
                    disabled={disabled || atMax}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                        setFocused(false);
                        // Commit a half-typed tag on blur so it isn't silently lost.
                        if (draft.trim()) addTag(draft);
                    }}
                    placeholder={value.length === 0 ? placeholder : atMax ? "Tag limit reached" : ""}
                    className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
                />
            </div>

            {focused && filteredSuggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
                    {filteredSuggestions.map((s) => (
                        <button
                            key={s}
                            type="button"
                            // onMouseDown (not onClick) so it fires before input blur.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addTag(s);
                            }}
                            className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
